<script setup lang="ts">
import { TRANSITION_SOURCE } from '#shared/constant/constant'
import type { BodyTypesResponse } from '#shared/interfaces/top/bodyTypes'
import { useCatalogSearchStore } from '@/stores/catalogSearch'
import {
  getBodyTypeTab,
  type BodyTypeTabTitle,
} from '@/constant/brandCarParams'
import { resolveBodyTypeIconUrl } from '@/utils/bodyTypeIcon'

const { tabTitle, contents } = defineProps<{
  tabTitle: BodyTypeTabTitle
  contents: BodyTypesResponse
}>()
// タブ固有の値(fuel_cd / アイコンのサブディレクトリ)は定数側に集約している
const bodyTypeTab = computed(() => getBodyTypeTab(tabTitle))

const checkList = ref<Array<string>>([])
const allCheckedList = ref<Array<string>>([])
const allSelectFlg = ref(false)
const closeModalRef = ref<HTMLButtonElement | null>(null)
const catalogSearchStore = useCatalogSearchStore()
// 参照専用。更新は必ずストアのアクション経由で行うため、書き換え不可の computed で取り出す
const displayIndex = computed(() => catalogSearchStore.displayIndex)
const clickTag = ref(false)

onMounted(() => {
  clickTag.value = false
  checkList.value = []
})

/**
 * ボディタイプアイコンのファイル名はbody_sub_cdに変更したものを保持している
 */
const generateBodyTypeAssetImagePath = (bodySubCd: string | undefined) =>
  resolveBodyTypeIconUrl(bodyTypeTab.value.iconDir, bodySubCd)

const submitSearch = () => {
  const fuelCd = bodyTypeTab.value.fuelCd
  if (checkList.value.length === 0) return
  closeModalRef.value?.click()
  const checkdBodyCdList: string[] = []
  // 直前の length === 0 早期 return により先頭要素は必ず存在する
  const firstChecked = checkList.value[0]
  if (firstChecked !== undefined && firstChecked === allCheckedList.value[0]) {
    checkdBodyCdList.push(firstChecked)
  } else {
    checkdBodyCdList.push(...checkList.value)
  }
  if (fuelCd) {
    navigateTo({
      path: 'car-model-list',
      query: {
        body_sub_cd: checkdBodyCdList.join(),
        fuel_cd: fuelCd,
        transition_source: TRANSITION_SOURCE.catalog_search,
      },
    })
  } else {
    navigateTo({
      path: 'car-model-list',
      query: {
        body_sub_cd: checkdBodyCdList.join(),
        transition_source: TRANSITION_SOURCE.catalog_search,
      },
    })
  }
  catalogSearchStore.setDisplayIndex(-1)
}

const toNextPage = (bodySubCd: string) => {
  if (!clickTag.value) {
    clickTag.value = true
    const fuelCd = bodyTypeTab.value.fuelCd
    closeModalRef.value?.click()
    navigateTo({
      path: '/car-model-list',
      query: {
        body_sub_cd: bodySubCd,
        fuel_cd: fuelCd,
        transition_source: TRANSITION_SOURCE.catalog_search,
      },
    })
  }
}

const clickEvent = (index: number) => {
  allCheckedList.value = []
  allCheckedList.value =
    contents.data[displayIndex.value]?.items.map((obj) => obj.body_sub_cd) ?? []
  if (0 === index) {
    allSelectFlg.value = !allSelectFlg.value
    if (!allSelectFlg.value) {
      checkList.value = []
    } else {
      checkList.value = [...allCheckedList.value]
    }
  } else if (
    !allSelectFlg.value &&
    allCheckedList.value.length - checkList.value.length === 1
  ) {
    checkList.value = [...allCheckedList.value]
    allSelectFlg.value = true
  } else if (allSelectFlg.value) {
    // delete first cell (select all)
    checkList.value.shift()
    allSelectFlg.value = false
  }
}

watch(
  () => displayIndex.value,
  () => {
    if (contents && displayIndex.value !== -1) {
      checkList.value = []
      allSelectFlg.value = false
    }
  },
  { deep: true }
)

const isActive = computed(() => {
  if (checkList.value.length !== 0) {
    return true
  }
})
</script>
<template>
  <div
    id="bodyTypeSubModal"
    class="modal fade"
    tabindex="1"
    aria-labelledby="bodyTypeSubModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div v-if="displayIndex >= 0" class="modal-content">
        <div class="modal-header">
          <div id="bodyTypeSubModalLabel" class="modal-title">
            {{
              contents.data[displayIndex]?.body_type_title.replace(
                'ハッチバック',
                'コンパクトカー'
              )
            }}の種類を選択
          </div>
        </div>
        <div class="modal-body">
          <div
            v-for="(item, i) in contents.data[displayIndex]?.items ?? []"
            :key="item.body_sub_cd"
            class="row item-body"
          >
            <label class="checkbox-area" :for="String(item.body_sub_cd)">
              <div class="col-2 col-check">
                <div class="form-check">
                  <input
                    :id="String(item.body_sub_cd)"
                    v-model="checkList"
                    class="form-check-input"
                    type="checkbox"
                    :value="item.body_sub_cd"
                    @change="clickEvent(i)"
                  />
                </div>
              </div>
              <div class="col-10">
                <div class="row">
                  <div class="col-3 col-image">
                    <img
                      class="bodytype-img"
                      :src="generateBodyTypeAssetImagePath(item.body_sub_cd)"
                    />
                  </div>
                  <div class="col-6 name-area">
                    <div
                      v-if="item.body_sub_cd === '018'"
                      class="body-type-name"
                    >
                      {{
                        item.body_sub_name.replace(
                          'ハッチバック',
                          'コンパクトカー'
                        )
                      }}
                    </div>
                    <div v-else class="body-type-name">
                      {{ item.body_sub_name }}
                    </div>
                  </div>
                  <div
                    class="col-2 col-icon"
                    @click="toNextPage(item.body_sub_cd)"
                  >
                    <div>
                      <img
                        style="width: 24px; height: 24px"
                        src="@/assets/image/icon/arrow_right.svg"
                      />
                    </div>
                  </div>
                </div></div
            ></label>
          </div>
        </div>
        <div class="container" :class="{ active: isActive }" style="margin: 0">
          <div class="row">
            <div class="col-6">
              <button
                ref="closeModalRef"
                type="button"
                class="btn close"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
            </div>
            <div class="col-6">
              <button
                type="button"
                class="btn submit"
                :disabled="!isActive"
                @click="submitSearch"
              >
                検索する
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.checkbox-area {
  width: 100%;
  display: flex;
}

.modal .modal-content {
  border-radius: 12px;
}

.modal .modal-dialog {
  top: 50%;
  padding: 20px;
  bottom: 10%;
  left: 0;
  transform: translate(0%, -50%);
}

.modal-content .modal-header {
  background-color: #f7f8fa;
  border-radius: 12px 12px 0px 0px;
  max-height: 40px;
}

.modal-content .modal-title {
  width: 100%;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
}

.modal-content .condition-name {
  font-size: 14px;
  color: #333333;
  opacity: 80%;
}

.form-control {
  background-color: #ffffff;
  border: 1px solid #ecf0f2;
  border-radius: 7px;
  font-weight: bold;
  font-size: 14px;
  min-height: 40px;
}

.form-control:focus {
  background-color: #ffffff;
  box-shadow: none;
}

.condition-text {
  font-size: 12px;
  display: -webkit-box;
  line-height: 1.5;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #333333;
}

.modal-content .col-6 {
  text-align: left;
  min-height: 44px;
  min-width: 50%;
  display: flex;
}

.modal-content .col-6 button {
  width: 100%;
}

.modal-content .col-6:first-child {
  background-color: #d9d9d9;
  border-radius: 0px 0px 0px 12px;
}

.modal-content .col-6:last-child {
  background-color: #c4b2b2;
  border-radius: 0px 0px 12px 0px;
}

.modal-content .col-6 .close {
  color: #000000;
}

.modal-content .col-6 .submit {
  color: #ffffff;
  border: none;
}

.active .col-6:last-child {
  background-color: #df2020;
}

.body-types {
  margin-top: 10px;
}

.body-types .title-body-type {
  font-size: 12px;
  font-weight: bold;
}

.body-types .item-body {
  display: flex;
  align-items: center;
}

.body-types .list-body {
  background-color: #ffffff;
  margin-top: 5px;
}

.item-body .row {
  align-items: center;
}

.item-body .col-10 {
  padding-top: 5px;
  padding-bottom: 5px;
  border-bottom: 1px solid #0000001a;
}

.item-body .col-image {
  display: flex;
  justify-content: space-between;
}

.bodytype-img {
  width: 48px;
}

.name-area {
  width: 58%;
}

.item-body .col-icon {
  text-align: right;
}

.item-body .col-check {
  display: flex;
  align-items: center;
}

.list-body .bi-chevron-right {
  color: #df2020;
  font-size: 18px;
}

.body-types img {
  width: 50px;
}

.list-body .form-check-input {
  width: 25px;
  height: 25px;
}

.body-type-name {
  font-size: 12px;
  font-weight: bold;
  padding-top: 12px;
  vertical-align: middle;
}

.form-check-input:checked {
  background-color: #df2020;
  border-color: #df2020;
}

input[type='checkbox'] {
  height: 24px;
  width: 24px;
  vertical-align: middle;
  border: 1px solid #d5d5d5;
}
</style>
